import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SceneSystem } from './SceneSystem';
import { applyTronMaterial, applyStyle, setCurrentStyle, getCurrentStyle, clearEdgesCache } from '../core/tron-material';
import type { StyleName } from '../core/styles';
import { TILE_WORLD_SCALE, CHUNK_VISIBILITY_MARGIN } from '../core/constants';
import { generateCity, chunkKeyForGrid, chunkIndexForGrid, CHUNK_CELLS, type CityPlacement } from '../city/generator';
import { CityGrid } from '../city/pathfinding';
import { assetUrl } from '../../lib/assets';

interface ChunkRecord {
  group: THREE.Group;
  /** World-space AABB of the chunk (XZ plane). */
  minX: number; maxX: number; minZ: number; maxZ: number;
  inScene: boolean;
  /** Chunks containing scenario-targeted infrastructure are never culled. */
  pinned: boolean;
}

const INFRA_MODELS = new Set([
  'pump-station', 'cooling-tower', 'gas-tank', 'factory', 'warehouse',
  'water-tower', 'substation', 'office-building', 'pylon', 'excavator',
]);

// Scale = targetWorldSize / nativeSize. Target ~45-60 world units (1.5-2 cells) for most.
const INFRA_SCALE: Record<string, number> = {
  'pump-station': 3,     // native 14.8 → 44 world units (~1.5 cells)
  'factory': 2.2,        // native 20.5 → 45 world units (~1.5 cells)
  'warehouse': 1.6,      // native 38 → 61 world units (~2 cells)
  'office-building': 4,  // native 10.5 → 42 world units (~1.4 cells)
  'cooling-tower': 4,    // native 9 → 36 world units (~1.2 cells)
  'gas-tank': 3.5,       // native 11.2 → 39 world units (~1.3 cells)
  'water-tower': 5,      // native 6.2 → 31 world units (~1 cell)
  'substation': 5,       // native 7.1 → 35 world units (~1.2 cells)
  'pylon': 4,            // native 6 → 24 world units (~0.8 cells)
  'excavator': 6,        // native 5.3 → 32 world units (~1 cell)
};

// Models in /models/ root (not /models/city/)
const ROOT_MODELS = new Set([
  ...INFRA_MODELS,
  'tree-beech', 'tree-birch', 'tree-conifer', 'tree-lime',
  'tree-oak', 'tree-poplar', 'tree-round', 'kenney-tree',
  'bench-forest', 'fence-classic',
  'tile-plain_concrete', 'tile-park',
]);

const CATEGORY_MAP: Record<string, string> = {
  roads: 'roads',
  commercial: 'commercial',
  residential: 'residential',
  industrial: 'infrastructure',
  utility: 'utility',
  nature: 'nature',
  community: 'community',
};

interface LabelEntry {
  el: HTMLDivElement;
  worldX: number;
  worldZ: number;
  infraId?: string;
}

export class CitySystem {
  private scene: SceneSystem;
  private loader = (() => {
    const l = new GLTFLoader();
    // Kenney city GLBs reference an external "Textures/colormap.png" relative to
    // the .glb. The shared atlas lives at /models/Textures/, not /models/city/Textures/,
    // so pin texture resolution there explicitly.
    l.setResourcePath(assetUrl('/models/'));
    return l;
  })();
  private rawCache = new Map<string, THREE.Group>();
  private styledCache = new Map<string, THREE.Group>();
  private meshes: THREE.Object3D[] = [];
  private labels: LabelEntry[] = [];
  private labelUpdateId = 0;
  grid: CityGrid | null = null;
  private infraWorldPositions = new Map<string, [number, number]>();
  // Chunk streaming: meshes live in per-chunk groups added/removed from the
  // scene as the viewport moves. Meshes still exist individually (this.meshes),
  // so all scenario per-mesh ops are unchanged.
  private chunks = new Map<string, ChunkRecord>();
  // Camera-state signature of the last visibility pass; skip recompute when
  // the camera hasn't moved (the rAF loop ticks every frame regardless).
  private lastCamSig = '';

  constructor(scene: SceneSystem) {
    this.scene = scene;
  }

  getInfraPosition(id: string): [number, number] | null {
    return this.infraWorldPositions.get(id) ?? null;
  }

  async generate(onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const result = generateCity({ gridW: 80, gridH: 80, blockW: 7, blockH: 6 });

    // Build pathfinding grid
    this.grid = new CityGrid(result.gridW, result.gridH, result.roadCells);

    const uniqueIds = [...new Set(result.placements.map(p => p.modelId))];
    let loaded = 0;
    onProgress?.(0, uniqueIds.length);
    await Promise.all(
      uniqueIds.map(id =>
        this.loadModel(id).then(() => {
          loaded += 1;
          onProgress?.(loaded, uniqueIds.length);
        }),
      ),
    );

    const usedCombos = new Set(result.placements.map(p => `${p.modelId}|${p.category}`));
    for (const combo of usedCombos) {
      const [modelId, category] = combo.split('|');
      const raw = this.rawCache.get(modelId);
      if (!raw) continue;
      const styled = raw.clone();
      applyTronMaterial(styled, CATEGORY_MAP[category] ?? 'commercial');
      this.styledCache.set(combo, styled);
    }

    for (const placement of result.placements) {
      this.place(placement);
    }

    // Initial streaming pass so on-screen chunks are present before first paint.
    this.updateVisibleChunks();
    this.startLabelUpdates();
  }

  // Convert world position to grid coordinates
  worldToGrid(worldX: number, worldZ: number): [number, number] {
    return [Math.round(worldX / TILE_WORLD_SCALE), Math.round(worldZ / TILE_WORLD_SCALE)];
  }

  // Convert grid coordinates to world position
  gridToWorld(gx: number, gz: number): [number, number] {
    return [gx * TILE_WORLD_SCALE, gz * TILE_WORLD_SCALE];
  }

  // Find a path between two world positions, returns world coordinates
  findRoute(fromX: number, fromZ: number, toX: number, toZ: number): Array<[number, number]> | null {
    if (!this.grid) return null;
    const [sx, sz] = this.worldToGrid(fromX, fromZ);
    const [ex, ez] = this.worldToGrid(toX, toZ);
    const gridPath = this.grid.findPath(sx, sz, ex, ez);
    if (!gridPath) return null;
    return this.gridPathToWorld(gridPath);
  }

  private gridPathToWorld(path: Array<[number, number]>): Array<[number, number]> {
    return path.map(([gx, gz]) => this.gridToWorld(gx, gz));
  }

  /**
   * Ambient (non-scenario) traffic route: a continuous wander over the road
   * grid, returned in world coordinates. Hero/scenario vehicles use findRoute
   * (A*) instead — see VehicleSystem.
   */
  wanderRoute(maxLen = 40): Array<[number, number]> | null {
    if (!this.grid) return null;
    const cells = this.grid.wanderPath(maxLen);
    if (!cells) return null;
    return this.gridPathToWorld(cells);
  }

  private scenarioActive = false;
  private activeInfraIds = new Set<string>();

  enterScenarioMode(activeIds?: string[]): void {
    this.scenarioActive = true;
    this.activeInfraIds = new Set(activeIds ?? []);
  }

  exitScenarioMode(): void {
    this.scenarioActive = false;
    this.activeInfraIds.clear();
    this.undimAll();
  }

  dimNonHighlighted(): void {
    const isLight = getCurrentStyle() === 'light';
    const dimColor = new THREE.Color(isLight ? 0xaaaaaa : 0x243848);
    const dimEdgeColor = new THREE.Color(isLight ? 0xbbbbbb : 0x2f4a5a);
    const dimSurface = isLight ? 0xdddddd : 0x142430;

    for (const mesh of this.meshes) {
      if (mesh.userData.infraId && this.activeInfraIds.has(mesh.userData.infraId)) {
        continue;
      }

      const opacity = mesh.userData.isRoad ? 0.65 : 0.55;

      mesh.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const mat = child.material as THREE.MeshPhongMaterial;
          mat.color.set(dimSurface);
          mat.emissive.copy(dimColor);
          mat.emissiveIntensity = 0.2;
          mat.transparent = true;
          mat.opacity = opacity;
        }
        if (child instanceof THREE.LineSegments && child.material) {
          const lm = child.material as THREE.LineBasicMaterial;
          lm.color.copy(dimEdgeColor);
          lm.transparent = true;
          lm.opacity = opacity;
        }
      });
    }
  }

  // Restore everything to current style colors
  undimAll(): void {
    for (const mesh of this.meshes) {
      const cat = mesh.userData.originalCategory || 'commercial';
      applyTronMaterial(mesh, CATEGORY_MAP[cat] ?? cat);
    }
  }

  private vehicleSystem: { undimAll: () => void } | null = null;

  setVehicleSystem(vs: { undimAll: () => void }): void {
    this.vehicleSystem = vs;
  }

  switchStyle(styleName: StyleName): void {
    setCurrentStyle(styleName);
    this.scene.applyStyle(styleName);
    for (const mesh of this.meshes) {
      const cat = mesh.userData.originalCategory || 'commercial';
      applyStyle(mesh, CATEGORY_MAP[cat] ?? cat, styleName);
    }
    // Re-apply to vehicles too
    this.vehicleSystem?.undimAll();
  }

  /**
   * Undim/highlight buildings near a world coordinate. Used by scenarios to
   * progressively reveal houses (e.g. customer lettering reaching properties).
   */
  highlightBuildingsNear(worldX: number, worldZ: number, radius = 80): void {
    for (const mesh of this.meshes) {
      if (mesh.userData.isRoad || mesh.userData.isUtility) continue;
      if (mesh.userData.infraId && this.activeInfraIds.has(mesh.userData.infraId)) continue;
      const cat = mesh.userData.originalCategory || 'commercial';
      if (cat === 'nature') continue;

      const dx = mesh.position.x - worldX;
      const dz = mesh.position.z - worldZ;
      if (Math.sqrt(dx * dx + dz * dz) < radius) {
        applyTronMaterial(mesh, CATEGORY_MAP[cat] ?? cat);
      }
    }
  }

  setFocusPoint(worldX: number, worldZ: number, radius = 150): void {
    if (!this.scenarioActive) return;
    for (const mesh of this.meshes) {
      if (mesh.userData.isRoad || mesh.userData.isUtility) continue;

      const dx = mesh.position.x - worldX;
      const dz = mesh.position.z - worldZ;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < radius) {
        const fade = Math.max(0.1, (dist / radius) * 0.4);
        mesh.traverse((child) => {
          if (child instanceof THREE.Mesh && child.material) {
            (child.material as THREE.Material).opacity = fade;
          }
        });
      }
    }
  }

  clearFocus(): void {
    if (!this.scenarioActive) return;
    this.dimNonHighlighted();
  }

  dispose(): void {
    for (const label of this.labels) label.el.remove();
    this.labels = [];
    cancelAnimationFrame(this.labelUpdateId);
    for (const mesh of this.meshes) {
      this.scene.remove(mesh);
      mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material?.dispose();
        }
      });
    }
    for (const rec of this.chunks.values()) {
      if (rec.inScene) this.scene.remove(rec.group);
    }
    this.chunks.clear();
    this.meshes = [];
    this.rawCache.clear();
    this.styledCache.clear();
    clearEdgesCache();
    this.lastCamSig = '';
    this.grid = null;
  }

  /** Lazily create the chunk record for a grid coordinate. */
  private getChunk(gridX: number, gridZ: number): ChunkRecord {
    const key = chunkKeyForGrid(gridX, gridZ);
    let rec = this.chunks.get(key);
    if (!rec) {
      const [cx, cz] = chunkIndexForGrid(gridX, gridZ);
      // Chunk spans CHUNK_CELLS cells; pad ±0.5 cell each side so model
      // footprints overhanging their cell boundary aren't culled early.
      const pad = 0.5;
      const span = (CHUNK_CELLS + 2 * pad) * TILE_WORLD_SCALE;
      const minX = (cx * CHUNK_CELLS - pad) * TILE_WORLD_SCALE;
      const minZ = (cz * CHUNK_CELLS - pad) * TILE_WORLD_SCALE;
      rec = {
        group: new THREE.Group(),
        minX,
        maxX: minX + span,
        minZ,
        maxZ: minZ + span,
        inScene: false,
        pinned: false,
      };
      this.chunks.set(key, rec);
    }
    return rec;
  }

  /**
   * Streaming: add chunk groups intersecting the viewport (plus a margin) to
   * the scene, recycle the rest out. Pinned chunks (scenario infra) always
   * stay. Called every frame but short-circuits unless the camera moved.
   */
  private updateVisibleChunks(): void {
    if (!this.scene.map) return;
    const map = this.scene.map;
    const c = map.getCenter();
    const sig = `${c.lng.toFixed(5)},${c.lat.toFixed(5)},${map.getZoom().toFixed(2)},${map.getBearing().toFixed(1)},${map.getPitch().toFixed(1)}`;
    if (sig === this.lastCamSig) return;
    this.lastCamSig = sig;

    const b = map.getBounds();
    if (!b) return;

    // Project the viewport's lng/lat corners into world space and take the AABB.
    const corners: Array<[number, number]> = [
      [b.getWest(), b.getNorth()],
      [b.getEast(), b.getNorth()],
      [b.getWest(), b.getSouth()],
      [b.getEast(), b.getSouth()],
    ];
    let vMinX = Infinity, vMaxX = -Infinity, vMinZ = Infinity, vMaxZ = -Infinity;
    for (const [lng, lat] of corners) {
      const w = this.scene.lngLatToWorld(lng, lat);
      vMinX = Math.min(vMinX, w.x); vMaxX = Math.max(vMaxX, w.x);
      vMinZ = Math.min(vMinZ, w.z); vMaxZ = Math.max(vMaxZ, w.z);
    }
    const m = CHUNK_VISIBILITY_MARGIN;
    vMinX -= m; vMaxX += m; vMinZ -= m; vMaxZ += m;

    for (const rec of this.chunks.values()) {
      const visible =
        rec.pinned ||
        (rec.maxX >= vMinX && rec.minX <= vMaxX &&
         rec.maxZ >= vMinZ && rec.minZ <= vMaxZ);
      if (visible && !rec.inScene) {
        this.scene.add(rec.group);
        rec.inScene = true;
      } else if (!visible && rec.inScene) {
        this.scene.remove(rec.group);
        rec.inScene = false;
      }
    }
  }

  private place(placement: CityPlacement): void {
    const key = `${placement.modelId}|${placement.category}`;
    const template = this.styledCache.get(key);
    if (!template) return;

    const mesh = template.clone();

    const x = placement.gridX * TILE_WORLD_SCALE;
    const z = placement.gridZ * TILE_WORLD_SCALE;
    mesh.position.set(x, 0, z);

    const isInfra = INFRA_MODELS.has(placement.modelId);
    const isNature = placement.category === 'nature';
    const baseScale = isInfra ? (INFRA_SCALE[placement.modelId] ?? 2) : isNature ? 8 : TILE_WORLD_SCALE;
    mesh.scale.setScalar(baseScale * placement.scale);
    mesh.rotation.y = placement.rotation;
    mesh.userData.isRoad = placement.category === 'roads';
    mesh.userData.isUtility = placement.category === 'utility';
    mesh.userData.originalCategory = placement.category;
    if (placement.id) {
      mesh.userData.infraId = placement.id;
      this.infraWorldPositions.set(placement.id, [x, z]);
    }

    // Park the mesh in its chunk group; the group is added to the scene
    // lazily by updateVisibleChunks() when it enters the viewport.
    const chunk = this.getChunk(placement.gridX, placement.gridZ);
    if (placement.id) chunk.pinned = true; // scenario infra: never cull
    chunk.group.add(mesh);
    this.meshes.push(mesh);

    // Labels for utility infrastructure
    if (placement.label && this.scene.map) {
      const labelEl = document.createElement('div');
      labelEl.textContent = placement.label;
      labelEl.style.cssText = 'position:absolute;pointer-events:none;font-size:9px;font-family:Inter,sans-serif;font-weight:600;color:#ff6644;text-shadow:0 0 4px rgba(0,0,0,0.5);white-space:nowrap;transform:translate(-50%,-100%);padding:2px 6px;background:var(--panel-bg);border-radius:3px;border:1px solid var(--border);';
      this.scene.map.getCanvasContainer().appendChild(labelEl);
      this.labels.push({ el: labelEl, worldX: x, worldZ: z, infraId: placement.id });
    }
  }

  private startLabelUpdates(): void {
    let lastLabelSig = '';
    const update = () => {
      if (!this.scene.map) return;
      this.updateVisibleChunks();
      const map = this.scene.map;
      const zoom = map.getZoom();

      // Labels are world-anchored: their screen position only changes when the
      // camera does. Skip the per-label project()+style writes (forced reflow)
      // while the camera is static.
      const c = map.getCenter();
      const sig = `${c.lng.toFixed(5)},${c.lat.toFixed(5)},${zoom.toFixed(2)},${map.getBearing().toFixed(1)},${map.getPitch().toFixed(1)},${this.scenarioActive ? 1 : 0}`;
      if (sig === lastLabelSig) {
        this.labelUpdateId = requestAnimationFrame(update);
        return;
      }
      lastLabelSig = sig;

      const showLabels = zoom >= 15;
      for (const label of this.labels) {
        if (!showLabels) { label.el.style.display = 'none'; continue; }
        // In scenario mode, only show labels for active infra
        if (this.scenarioActive) {
          if (!label.infraId || !this.activeInfraIds.has(label.infraId)) {
            label.el.style.display = 'none';
            continue;
          }
        }
        label.el.style.display = '';
        const lngLat = this.scene.worldToLngLat(new THREE.Vector3(label.worldX, 0, label.worldZ));
        const point = this.scene.map.project(lngLat);
        label.el.style.left = point.x + 'px';
        label.el.style.top = (point.y - 25) + 'px';
      }

      this.labelUpdateId = requestAnimationFrame(update);
    };
    this.labelUpdateId = requestAnimationFrame(update);
  }

  private async loadModel(modelId: string): Promise<void> {
    if (this.rawCache.has(modelId)) return;
    const path = assetUrl(ROOT_MODELS.has(modelId) ? `/models/${modelId}.glb` : `/models/city/${modelId}.glb`);
    try {
      const gltf = await this.loader.loadAsync(path);
      this.rawCache.set(modelId, gltf.scene);
    } catch (e) {
      console.warn(`[CitySystem] Failed to load ${modelId}:`, e);
    }
  }
}
